/**
 * MYIHTP — Mod. 16 (Reação do Formador): consulta/edição pelo próprio
 * formador autenticado. Se o curso estiver Encerrado, é apenas consulta.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, CheckCircle2, GraduationCap, Loader2, Lock, Save, Info,
} from "lucide-react";
import { myApi } from "../api";

export default function MyFormadorAvaliacao() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [answers, setAnswers] = useState({});
  const [sugestoes, setSugestoes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let alive = true;
    myApi.formadorAvaliacao(id)
      .then((d) => {
        if (!alive) return;
        // Pre-carrega respostas existentes (se houver)
        if (d.submitted?.answers) setAnswers(d.submitted.answers);
        if (d.submitted?.sugestoes) setSugestoes(d.submitted.sugestoes);
        setState({ loading: false, error: "", data: d });
      })
      .catch((e) => {
        if (alive) setState({ loading: false, error: e.message || "Erro a carregar avaliação.", data: null });
      });
    return () => { alive = false; };
  }, [id]);

  const data = state.data;
  const canEdit = !!data?.can_edit;

  const allAnswered = useMemo(() => {
    if (!data?.questions) return false;
    return data.questions.every((q) => Number.isInteger(answers[q.key])
      && answers[q.key] >= 1 && answers[q.key] <= 5);
  }, [data, answers]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!allAnswered || !canEdit) return;
    setSubmitting(true);
    try {
      await myApi.formadorAvaliacaoSubmit(id, {
        ...answers, sugestoes: sugestoes.trim() || undefined,
      });
      setSaved(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      // Refresh submission info
      const d = await myApi.formadorAvaliacao(id);
      setState({ loading: false, error: "", data: d });
    } catch (err) {
      alert(err.message || "Erro ao guardar avaliação.");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="py-20 grid place-items-center text-sm text-[#6B7280]" data-testid="my-formador-avaliacao-loading">
        <Loader2 className="animate-spin mb-2" size={18} /> A carregar…
      </div>
    );
  }
  if (state.error || !data) {
    return (
      <div data-testid="my-formador-avaliacao-error" className="max-w-2xl mx-auto bg-white rounded-[20px] border border-[#E6E1D6] p-8">
        <h1 className="text-2xl text-[#0F1829] mb-2" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
          Não foi possível abrir a avaliação
        </h1>
        <p className="text-sm text-[#6B7280]">{state.error}</p>
        <button
          onClick={() => navigate("/my/")}
          className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0F1829] text-white text-sm hover:bg-[#1a2542]"
        >
          <ArrowLeft size={14} /> Voltar
        </button>
      </div>
    );
  }

  const c = data.curso;
  const submitted = data.submitted;
  const readOnly = !canEdit;

  return (
    <div className="space-y-6" data-testid="my-formador-avaliacao-page">
      <Link
        to="/my/"
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#978D78] hover:text-[#0F1829]"
      >
        <ArrowLeft size={13} /> Voltar
      </Link>

      {/* Header */}
      <div className="bg-white rounded-[20px] border border-[#E6E1D6] p-6 lg:p-8">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-full bg-[#F5F2EB] text-[#6F4F1D] grid place-items-center shrink-0">
            <GraduationCap size={22} strokeWidth={1.7} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#978D78] mb-1">
              Mod. 16 — Avaliação de Reação do Formador
            </div>
            <h1
              className="text-2xl lg:text-3xl text-[#0F1829] leading-tight"
              style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}
              data-testid="my-formador-avaliacao-curso"
            >
              {c.nome_curso}
              {c.edicao && <span className="text-[#978D78] text-lg"> · Edição {c.edicao}</span>}
            </h1>
            <div className="text-xs text-[#6B7280] mt-1.5">
              {c.data_inicio ? c.data_inicio.slice(0, 10) : ""}
              {c.data_fim ? ` → ${c.data_fim.slice(0, 10)}` : ""}
              {c.local && <span> · {c.local}</span>}
              {c.formador && <span> · Formador: <strong className="text-[#0F1829]">{c.formador}</strong></span>}
            </div>
          </div>
          {readOnly && (
            <span
              data-testid="my-formador-avaliacao-locked-badge"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] bg-[#F5F2EB] text-[#6F4F1D] border border-[#E6E1D6]"
              title="Curso encerrado"
            >
              <Lock size={11} /> Curso encerrado · só consulta
            </span>
          )}
        </div>

        {saved && (
          <div
            data-testid="my-formador-avaliacao-saved"
            className="mt-5 px-4 py-2.5 rounded-[14px] text-sm flex items-center gap-2 bg-[#F0F5E8] text-[#4A5E2E] border border-[#6F7559]/30"
          >
            <CheckCircle2 size={16} /> Avaliação guardada com sucesso.
          </div>
        )}
        {readOnly && submitted && (
          <div className="mt-5 px-4 py-2.5 rounded-[14px] text-sm flex items-center gap-2 bg-[#F5F2EB] text-[#6F4F1D] border border-[#E6E1D6]">
            <Info size={14} /> Submetida em{" "}
            {submitted.data_submissao ? submitted.data_submissao.slice(0, 10) : "—"}.
            Como o curso está encerrado, a avaliação já não pode ser alterada.
          </div>
        )}
        {readOnly && !submitted && (
          <div className="mt-5 px-4 py-2.5 rounded-[14px] text-sm flex items-center gap-2 bg-[#FFF7E6] text-[#7A5C18] border border-[#F0D8A8]">
            <Info size={14} /> Este curso está encerrado e não foi submetida qualquer avaliação.
          </div>
        )}
      </div>

      {/* Formulário */}
      <form onSubmit={onSubmit} className="bg-white rounded-[20px] border border-[#E6E1D6] p-6 lg:p-8">
        <h2
          className="text-lg text-[#0F1829] mb-1"
          style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}
        >
          Avaliação Geral
        </h2>
        <p className="text-xs text-[#6B7280] mb-5">
          Indique a sua opinião sincera sobre o presente curso de formação, marcando na escala 1-5 cada item.
        </p>

        <ul className="space-y-4">
          {data.questions.map((q) => (
            <ScaleRow
              key={q.key}
              q={q}
              value={answers[q.key]}
              onChange={(v) => setAnswers((prev) => ({ ...prev, [q.key]: v }))}
              disabled={readOnly}
            />
          ))}
        </ul>

        <div className="mt-6">
          <label className="text-[10px] uppercase tracking-[0.2em] text-[#978D78] font-medium mb-1.5 block">
            Sugestões / Críticas (opcional)
          </label>
          <textarea
            value={sugestoes}
            onChange={(e) => setSugestoes(e.target.value)}
            rows={4}
            maxLength={4000}
            disabled={readOnly}
            data-testid="my-formador-avaliacao-sugestoes"
            className="w-full px-4 py-2.5 border border-[#E6E1D6] rounded-[14px] text-sm text-[#0F1829] focus:outline-none focus:ring-2 focus:ring-[#978D78] bg-white transition disabled:bg-[#FAF8F3] disabled:cursor-not-allowed"
          />
          <p className="text-[10px] text-[#978D78] text-right mt-1">
            {sugestoes.length} / 4000
          </p>
        </div>

        {!readOnly && (
          <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-[#6B7280]">
              {allAnswered
                ? "✓ Todas as perguntas respondidas."
                : "Por favor responda a todas as perguntas antes de submeter."}
            </p>
            <button
              type="submit"
              disabled={!allAnswered || submitting}
              data-testid="my-formador-avaliacao-submit-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0F1829] text-white text-sm rounded-full hover:bg-[#1a2542] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed shadow-[2px_2px_12px_rgba(15,24,41,0.15)]"
            >
              {submitting ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              {submitting ? "A guardar…" : submitted ? "Actualizar avaliação" : "Submeter avaliação"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

function ScaleRow({ q, value, onChange, disabled }) {
  return (
    <li className="border border-[#F3F0E7] rounded-[14px] p-4" data-testid={`q-${q.key}`}>
      <p className="text-sm text-[#0F1829] mb-2.5">
        {q.label} {!disabled && <span className="text-[#B91C1C]">*</span>}
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-[#978D78] min-w-[80px]">{q.low}</span>
        {[1, 2, 3, 4, 5].map((n) => {
          const active = value === n;
          return (
            <button
              type="button"
              key={n}
              onClick={() => !disabled && onChange(n)}
              disabled={disabled}
              data-testid={`scale-${q.key}-${n}`}
              aria-label={`${q.label}: ${n}`}
              className={`w-9 h-9 rounded-full border-2 text-sm font-semibold transition ${
                active
                  ? "bg-[#0F1829] text-white border-[#0F1829]"
                  : "bg-white text-[#6B7280] border-[#E6E1D6] hover:border-[#0F1829]"
              } ${disabled ? "cursor-not-allowed opacity-70" : ""}`}
            >
              {n}
            </button>
          );
        })}
        <span className="text-[10px] uppercase tracking-wider text-[#978D78] min-w-[100px] text-right">{q.high}</span>
      </div>
    </li>
  );
}
